'use client'

'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  ArrowRight,
  Newspaper,
  Users,
  Building2,
  Megaphone
} from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { newsArticles } from '@/data/news-articles'

// アイコンマッピング
import { FadeUp } from '@/components/ui/fade-up'
const categoryIcons: Record<string, typeof Newspaper> = {
  'プレスリリース': Megaphone,
  'お知らせ': Building2,
  'メディア掲載': Newspaper,
  'イベント': Users
}

// 日付フォーマット変換関数
function formatDate(dateStr: string): string {
  return dateStr.replace(/\//g, '.')
}

// HTMLからテキストを抽出してサマリーを作成
function createSummary(html: string, maxLength = 100): string {
  const text = html.replace(/<[^>]*>/g, '').trim()
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// 実データを画面表示用に変換
const newsItems = newsArticles.map((article) => ({
  id: article.id,
  date: formatDate(article.date),
  category: article.category,
  title: article.title,
  summary: createSummary(article.content),
  featured: article.id === 'mihia_vibe_coding_20260522' || article.id === 'published_2603hkreport' || article.id === 'published_20251111chugoku_np' || article.id === 'published_20251105chugoku_np' || article.id === 'digital_pride_hiroshima_event_report_20251006' || article.id === 'digital_pride_hiroshima_open_2025', // ピックアップ記事
  icon: categoryIcons[article.category] || Newspaper,
  image: article.image
}))

const categories = [
  { name: 'すべて', count: newsItems.length },
  { name: 'プレスリリース', count: newsItems.filter(n => n.category === 'プレスリリース').length },
  { name: 'お知らせ', count: newsItems.filter(n => n.category === 'お知らせ').length },
  { name: 'メディア掲載', count: newsItems.filter(n => n.category === 'メディア掲載').length }
]

export function NewsContent() {
  const [selectedCategory, setSelectedCategory] = useState('すべて')
  
  const featuredNews = newsItems.filter(item => item.featured)
  const filteredNews = selectedCategory === 'すべて'
    ? newsItems
    : newsItems.filter(item => item.category === selectedCategory)

  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-background py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4">最新情報をお届け</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              ニュース
            </h1>
            <p className="text-xl text-muted-foreground">
              CodeFoxの最新ニュース、プレスリリース、<br />
              お知らせをご覧いただけます
            </p>
          </div>
        </div>
      </section>

      {/* カテゴリーフィルター */}
      <section className="py-8 border-b">
        <div className="container">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <Button
                key={category.name}
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCategory(category.name)}
                className={selectedCategory === category.name 
                  ? 'bg-[#292623] text-white hover:bg-[#292623]/90' 
                  : 'border border-[#292623]/20 hover:bg-[#292623]/5'
                }
              >
                {category.name}
                <Badge 
                  variant="secondary" 
                  className={`ml-2 ${selectedCategory === category.name ? 'bg-white/20 text-white' : 'bg-[#BEB0A8]/30 text-[#292623]'}`}
                >
                  {category.count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* 注目のニュース */}
      {selectedCategory === 'すべて' && (
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-8">注目のニュース</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {featuredNews.map((news) => {
                const Icon = news.icon
                return (
                  <FadeUp
                    key={news.id}
                  >
                    <Link href={`/news/${news.id}`}>
                      <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="aspect-video bg-gray-100 relative">
                          <Image
                            src={news.image || '/images/placeholder.jpg'}
                            alt={news.title}
                            fill
                            className="object-cover object-top"
                          />
                        </div>
                        <CardHeader>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{news.category}</Badge>
                            <span className="text-sm text-muted-foreground">{news.date}</span>
                          </div>
                          <CardTitle className="flex items-start gap-3">
                            <Icon className="h-6 w-6 text-primary shrink-0 mt-1" />
                            <span>{news.title}</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-muted-foreground mb-4">{news.summary}</p>
                          <Button variant="ghost" className="p-0 h-auto">
                            続きを読む
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    </Link>
                  </FadeUp>
                )
              })}
            </div>
          </FadeUp>
        </div>
      </section>
      )}

      {/* その他のニュース */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <h2 className="text-2xl font-bold mb-8">すべてのニュース</h2>

          <div className="space-y-4">
            {filteredNews.map((news) => {
              const Icon = news.icon
              return (
                <FadeUp
                  key={news.id}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <div className="shrink-0">
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon className="h-6 w-6 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              {news.category}
                            </Badge>
                            <span className="text-sm text-muted-foreground">{news.date}</span>
                          </div>
                          <h3 className="font-semibold mb-2">
                            <Link href={`/news/${news.id}`} className="hover:text-primary transition-colors">
                              {news.title}
                            </Link>
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {news.summary}
                          </p>
                        </div>
                        <div className="shrink-0 hidden sm:block">
                          <Button variant="ghost" size="sm" asChild>
                            <Link href={`/news/${news.id}`}>
                              <ArrowRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>

          {/* ページネーション */}
          <div className="flex justify-center mt-8 gap-2">
            <Button variant="outline" size="sm" disabled>
              前へ
            </Button>
            <Button variant="default" size="sm">1</Button>
            <Button variant="outline" size="sm">2</Button>
            <Button variant="outline" size="sm">3</Button>
            <Button variant="outline" size="sm">
              次へ
            </Button>
          </div>
        </div>
      </section>

    </>
  )
}